/**
 * Diffing primitives, shared by every diff view.
 *
 * The algorithm is the same longest-common-subsequence walk `DiffModal` has always used for lines; it is only
 * generalised over the ITEMS being compared, so the same code can diff lines or words. That generality is the whole
 * point: a line diff is the right view for versioned settings and a poor one for prose, where a reworded sentence
 * marks a whole paragraph removed and re-added.
 */

import { isEmpty } from 'lodash'

/**
 * The longest sequence of items appearing in both inputs, in order (classic LCS by dynamic programming).
 *
 * @param {Array<string>} left
 * @param {Array<string>} right
 * @returns {Array<string>} the common subsequence
 */
export const longestCommonSubsequence = (left, right) => {
  const table = Array(left.length + 1)
    .fill(null)
    .map(() => Array(right.length + 1).fill(0))

  for (let i = 1; i <= left.length; i++) {
    for (let j = 1; j <= right.length; j++) {
      if (left[i - 1] === right[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1])
      }
    }
  }

  const common = []
  let leftIdx = left.length
  let rightIdx = right.length

  while (leftIdx > 0 && rightIdx > 0) {
    if (left[leftIdx - 1] === right[rightIdx - 1]) {
      common.unshift(left[leftIdx - 1])
      leftIdx--
      rightIdx--
    } else if (table[leftIdx - 1][rightIdx] > table[leftIdx][rightIdx - 1]) {
      leftIdx--
    } else {
      rightIdx--
    }
  }

  return common
}

export const UNCHANGED = 'unchanged'
export const REMOVED = 'removed'
export const ADDED = 'added'

/**
 * Classify every item as unchanged / removed / added, in reading order.
 *
 * @param {Array<string>} oldItems
 * @param {Array<string>} newItems
 * @returns {Array<{type: string, value: string}>}
 */
export const computeDiff = (oldItems, newItems) => {
  const common = longestCommonSubsequence(oldItems, newItems)
  const result = []

  let oldIdx = 0
  let newIdx = 0
  let commonIdx = 0

  while (oldIdx < oldItems.length || newIdx < newItems.length) {
    const anchor = common[commonIdx]
    const oldMatches = commonIdx < common.length && oldItems[oldIdx] === anchor
    const newMatches = commonIdx < common.length && newItems[newIdx] === anchor

    if (oldMatches && newMatches) {
      result.push({ type: UNCHANGED, value: oldItems[oldIdx] })
      oldIdx++
      newIdx++
      commonIdx++
    } else if (!oldMatches && oldIdx < oldItems.length) {
      result.push({ type: REMOVED, value: oldItems[oldIdx] })
      oldIdx++
    } else if (newIdx < newItems.length) {
      result.push({ type: ADDED, value: newItems[newIdx] })
      newIdx++
    }
  }

  return result
}

// Split into words while KEEPING the whitespace as its own token, so a diff can be rendered back as the original
// text rather than a normalised approximation of it. Without this, re-joining would silently reflow the prompt.
export const toWords = (text) => (text || '').split(/(\s+)/).filter((token) => !isEmpty(token))

export const toLines = (text) => (text || '').split('\n')

/**
 * Group a diff into runs of the same type, so a view can treat "these four lines were replaced by those six" as one
 * edit. Rendering each line on its own is what makes a prose diff unreadable: the unit a reader cares about is the
 * passage that changed, not the line boundaries the text happens to have.
 *
 * @returns {Array<{type: string, values: Array<string>}>}
 */
export const groupRuns = (entries) => {
  const runs = []

  entries.forEach((entry) => {
    const current = runs[runs.length - 1]

    if (current?.type === entry.type) {
      current.values.push(entry.value)
    } else {
      runs.push({ type: entry.type, values: [entry.value] })
    }
  })

  return runs
}

/**
 * Turn line-level runs into the blocks a unified view renders: unchanged context, and CHANGES that pair a removed
 * run with the added run that replaced it.
 *
 * Pairing is what makes a word-level diff possible at all — the two halves have to be compared against each other,
 * and they are only adjacent runs in the line diff. It is also why the whole run is joined before comparing rather
 * than zipped line-for-line: an optimizer routinely turns one sentence into a four-item checklist, and a
 * line-for-line pairing renders that as an unrelated deletion followed by an unrelated insertion.
 *
 * @returns {Array<{type: 'unchanged'|'changed', lines?: Array<string>, before?: string, after?: string}>}
 */
export const toUnifiedBlocks = (oldText, newText) => {
  const runs = groupRuns(computeDiff(toLines(oldText), toLines(newText)))
  const blocks = []
  let index = 0

  while (index < runs.length) {
    const run = runs[index]
    const next = runs[index + 1]

    if (run.type === UNCHANGED) {
      blocks.push({ type: UNCHANGED, lines: run.values })
      index++
    } else if (run.type === REMOVED && next?.type === ADDED) {
      blocks.push({
        type: 'changed',
        before: run.values.join('\n'),
        after: next.values.join('\n'),
      })
      index += 2
    } else if (run.type === REMOVED) {
      blocks.push({ type: 'changed', before: run.values.join('\n'), after: '' })
      index++
    } else {
      blocks.push({ type: 'changed', before: '', after: run.values.join('\n') })
      index++
    }
  }

  return blocks
}
